<template lang="pug">
.page.page--light.pricing
  .page__inner
    .page__cover.pd-cover
      .pd-container
        .page__caption(data-aos="fade-down") Pricing
        .page__description(data-aos="zoom-out") The most affordable route to retail

    .page__section.page__section--dark.protect-your-price
      .pd-container
        .foreword.tc(data-aos="fade-in")
          h2.pd-h3 Protect Your Price
          p With Pod you control the price retailers pay for your products – a key distinction between Pod and traditional distributors. This means you&nbsp;
            span.highlight keep more, much more, of your margin
            | &nbsp;than you would otherwise. Use our Direct to Retail Price Calculator to see the margin you keep vs the traditional distributor pricing model.

        pricing-cost-calculator.calculator(data-aos="fade-up")

    .page__section.control-your-cost
      .pd-container
        .foreword.tc(data-aos="fade-in")
          h2.pd-h3 Control Your Cost
          p Pod offers two routes to retail, Pod Express and Pod Direct. Based on your inputs (how fast inventory turns, the volume of business Pod services on your behalf, your Direct to Retail price by region)&nbsp;
            span.highlight You determine your service cost
            | &nbsp;– and there's never a surprise. We readily tell you about all of our fees.

      .container
        .pricing-scroller(data-aos="fade-up")
          table
            tbody
              tr
                td
                  .row__name
                    img.cell__icon(src="/img/tag.express.svg")
                    br
                    | Your Pod Express Service Fee
                  .row__description Managed inventory and full-service distribution direct to retail,
                    br
                    | i.e. you send us inventory and we deliver it to retailers.
                td
                  .cell__value
                    strong 18%
                    br
                    | of your Direct to Retail Price less&nbsp;
                    a.linked(
                      :href="BRAND_GUIDE_STORAGE_FEES_URL"
                      target="_blank"
                      title="Brand Guide"
                      rel="noopenner") Storage Fees
                    | .
              tr
                td
                  .row__name
                    img.cell__icon(src="/img/tag.direct.svg?pod")
                    br
                    | Your Pod Direct Service Fee
                  .row__description DIY distribution direct from your inventory to retail,
                    br
                    | i.e. an order comes in and you ship it directly to the store.

                td
                  .cell__value
                    strong 8%
                    br
                    | of your Direct to Retail Price.

    .page__section.page__section--pull.comparison
      .pd-container
        .foreword.tc(data-aos="fade-in")
          h2.pd-h3 Ditch the Deductions &
            br
            | Maximize Your Margin
          p
            span.highlight Pod eliminates predatory fees and deductions.
            | &nbsp;If it wasn't clear in our Direct to Retail Price calculator that your margin gets eaten up by traditional distributors, let the below chart show you just where they get you
            br
            | (and wow, did we have to go deep to get this information!&nbsp;
            strong
              nuxt-link.linked(:to="{ name: 'pricing-earnings-calculator' }" title="Earnings Calculator") Learn more here.
            | )

      .pd-container.full-on-sm-and-down
        .details-scroller.page__scroller.page__scroller--x(data-aos="fade-up")
          table
            thead
              tr
                th &nbsp;
                th Incumbent National Distributors
                th.highlight Pod
            tbody
              tr
                td Distribution Margin
                td
                  span.red 26%
                td
                  span.orange 18%
              tr
                td Inventory Processing Fee
                td.none /
                td
                  span.orange $15 Single SKU Pallet, $25 Mixed SKU Pallet
              tr
                td Inventory Storage Fee
                td.none /
                td
                  span.orange $35 - $55 / pallet
                    br
                    | based on temperature
              tr
                td Early Pay Discount
                td
                  span.red 2%net15&nbsp;
                    strong mandatory
                td
                  span.optional 2%net15&nbsp;
                    strong optional
              tr
                td Freight Allowance
                td
                  span.red $ / lb
                td.none /

              tr
                td OI Discounts + Pricing Variance
                td
                  span.red 15%
                td.none /
              tr
                td Cut Case Fee
                td
                  span.red $1 / case
                td.none /
              tr
                td Promos (Ads/EDLP/TPR)
                td
                  span.red 5 - 20%
                td.none /
              tr
                td Merchandising
                td
                  span.red 1 - 3%
                td.none /
              tr
                td Free-fill or Slotting Fees
                td
                  span.red 1 - 2 cases per sku
                    br
                    | offered for free
                td.none /
              tr
                td Returns / Spoils
                td
                  span.red cost of product
                td.none /
              tr
                td Data program
                td
                  span.red 1.5 - 2%
                td.none /
              tr
                td New Item Setup Fee
                td
                  span.red $50 - $700
                td.none /
              tr
                td Ad Programs
                td
                  span.red $1,250 - $2,000
                td.none /
              tr
                td Data packages
                td
                  span.red from $650
                td.none /
              tr
                td New Item Intro Fees
                td
                  span.red 15%
                td.none /
              tr
                td Overpull
                td
                  span.red 15%
                td.none /
              tr
                td Short-Ships
                td
                  span.red 2%
                td.none /
              tr
                td Late Delivery Fee
                td
                  span.red $250
                td.none /
              tr
                td Ready Date Non-Compliance
                td
                  span.red $150
                td.none /
              tr
                td Lumper Fee
                td
                  span.red $100 - $500
                td.none /
              tr
                td Overship Fee
                td
                  span.red 35%
                td.none /
              tr
                td Service Level Fines
                td
                  span.red 3%
                td.none /
              tr
                td Short During A Promo
                td
                  span.red 15%
                td.none /
              tr
                td Spoilage Allowance
                td
                  span.red 0.25 - 2%
                td.none /
              tr
                td Late Disposition fee
                td
                  span.red $1,000
                td.none /
              tr
                td Pack Change Fee
                td
                  span.red $250 - $400
                td.none /
              tr
                td Events
                td
                  span.red $2,000 - $4,000
                td.none /
              tr
                td Admin Fees
                td
                  span.red $20 - $500
                    br
                    | + pass through 8 - 10%
                td.none /
              tr
                td Admin Fee for Promos
                td
                  span.red 0.5%
                td.none /
    .page__section.distribute-with-pod-foods.page__section--pull(data-aos="fade-in")
      .pd-container.framed
        .frost-panel(v-image="'/img/panel.distribute-with-pod-foods.webp'")
          .frost-panel__cell-left
            h2 Distribute with Pod
            p.caption Did we mention it takes a fraction of the time to get set up and on shelf at retail with Pod?
              br
              | Choose to make Pod your distribution solution.
            a.cta.el-button.el-button--primary(
              href="mailto:brands@podfoods.co"
              target="_blank"
              title="Get in touch") Get in touch with our team today
          .frost-panel__cell-right
            .blank &nbsp;
    //- book-a-call-section
</template>

<script>
import { BRAND_GUIDE_STORAGE_FEES_URL } from '~/settings'
import PricingCostCalculator from '~/components/PricingCostCalculator'

export default {
  components: {
    PricingCostCalculator
  },

  layout: 'landing',

  data () {
    return {
      BRAND_GUIDE_STORAGE_FEES_URL,
      content: null
    }
  },

  head () {
    return this.$seo.head({
      title: 'Pricing',
      description: 'Pod pricing and fees for distribution services. Pod Express and Pod Direct.'
    })
  }
}
</script>

<style lang="scss" scoped>
.pricing {
  overflow: hidden;
}
table {
  min-width: 100%;
  border-collapse: collapse;
  background: white;
  th, td {
    border: $--border-light;
    text-align: center;
    padding: 1.5em 1em;
    font-weight: $--font-weight-normal;
  }
  th.highlight {
    background: $--color-primary;
    color: white;
  }
}
.row {
  &__name {
    text-align: left;
    font-weight: $--font-weight-bold;
    color: $--color-text-regular;
    font-size: 1.15em;
  }
  &__description {
    text-align: left;
    color: $--color-text-secondary;
    margin-top: .5em;
  }
}
.cell {
  &__icon {
    margin-bottom: .35em;
  }
  &__caption {
    font-size: .9em;
    line-height: 1.5em;
    font-weight: $--font-weight-bold;
  }
  &__asterisk {
    color: $--color-danger;
    vertical-align: top;
    font-weight: $--font-weight-bold;
  }
  &__value {
    margin-top: .3em;
    color: $--color-text-secondary;
    em, strong {
      font-size: 1.35em;
      font-weight: $--font-weight-bold;
      font-style: normal;
      color: $--color-text-regular;
    }
    em {
      color: $--color-primary;
    }
  }
}
.foreword {
  max-width: 724px;
  margin-left: auto;
  margin-right: auto;
  p {
    em {
      font-style: normal;
      background-color: $--color-highlight;
      color: black;
    }
    .highlight {
      color: $--color-primary;
      font-weight: $--font-weight-bold;
    }
  }
}
.page__section {
  padding: 5em 0;
  &--pull {
    margin-top: -5em;
  }
  h2 {
    margin-bottom: .5em;
  }
}
.protect-your-price {
  overflow: hidden;
  background: #EFE2F1;
  margin: 0;
  @include media(md) {
    border-radius: 24px;
    margin: 3em 24px 0;
  }
  .foreword {
    h2 {
      color: $--color-text-regular;
    }
    p {
      color: $--color-text-secondary;
      .highlight {
        color: $--color-primary;
      }
    }
  }
  .calculator {
    margin-top: 0;
    @include media(md) {
      margin-top: 2em;
    }
  }
  ::v-deep {
    .el-card {
      margin-top: 1.5em;
    }
  }
}
.control-your-cost {
  table {
    width: 100%;
  }
  .features-logos {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0;
    flex-direction: column;
    @include media(md) {
      gap: 25px;
      flex-direction: row;
    }
  }
  .pricing-scroller {
    margin-top: 1.5em;
    @include media(sm) {
      margin-top: 3em;
    }
  }
}
.comparison {
  .foreword {
    h2 {
      margin-top: .25em;
    }
    p {
      .highlight {
        color: $--color-primary;
      }
    }
  }
  .details-scroller {
    margin-top: 1.5em;
    @include media(sm) {
      margin-top: 3em;
      overflow: hidden;
    }
    > * {
      @include media(xs-only) {
        margin-left: 12px;
        margin-right: 12px;
      }
    }
    table {
      .logo {
        width: 150px;
      }
      td {
        padding-top: 8px;
        padding-bottom: 8px;
        &:first-child {
          text-align: left;
        }
        &.none {
          background: darken(white, 3);
        }
        &:nth-child(2),
        &:nth-child(3) {
          width: 320px;
        }
      }
      span {
        display: inline-block;
        text-align: left;
        width: 180px;
        &.red,
        &.orange,
        &.optional {
          padding-left: 24px;
          background-repeat: no-repeat;
          background-position: left center;
          background-size: 16px 16px;
        }
        &.red {
          background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTciIHZpZXdCb3g9IjAgMCAxNiAxNyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xLjk3NDIxIDQuOTM1NTdDMS4zNTUzIDUuOTc5NiAxIDcuMTk4MzEgMSA4LjVDMSAxMi4zNjYgNC4xMzQwMSAxNS41IDggMTUuNUMxMS44NjYgMTUuNSAxNSAxMi4zNjYgMTUgOC41QzE1IDcuMTg0ODQgMTQuNjM3MyA1Ljk1NDQxIDE0LjAwNjUgNC45MDMyM0MxNC4zMDY0IDQuNDYwMzcgMTUuMzY2NiAyLjY5NzA0IDE0Ljc5MjMgMC42MjQzNDlDMTQuNzg2MSAwLjU5MzU0NiAxNC43NzA2IDAuNTY1MzkgMTQuNzQ3OSAwLjU0MzcxNkMxNC43MjUxIDAuNTIyMDQyIDE0LjY5NjMgMC41MDc5MDQgMTQuNjY1MiAwLjUwMzIyOEMxNC42MzQxIDAuNDk4NTUyIDE0LjYwMjQgMC41MDM1NjUgMTQuNTc0MyAwLjUxNzU4NEMxNC41NDYyIDAuNTMxNjAzIDE0LjUyMzEgMC41NTM5NDcgMTQuNTA4MSAwLjU4MTU3MUMxMy43NjY0IDEuNjkzNDYgMTIuNTI4OSAyLjQyMzk1IDExLjk1MTUgMi43MjExNEMxMC44MjY5IDEuOTUwNzIgOS40NjYwOSAxLjUgOCAxLjVDNi41MjU5OCAxLjUgNS4xNTgzNyAxLjk1NTYgNC4wMzAzMSAyLjczMzY3QzMuNDY1MDYgMi40NDQ4MiAyLjIwMTc4IDEuNzA5MjggMS40NTQ5OSAwLjU4MDA0NkMxLjQ0MDAzIDAuNTUyNDIxIDEuNDE2OTMgMC41MzAwNzcgMS4zODg4MiAwLjUxNjA1OEMxLjM2MDcgMC41MDIwMzkgMS4zMjg5NiAwLjQ5NzAyNiAxLjI5Nzg5IDAuNTAxNzAyQzEuMjY2ODMgMC41MDYzNzkgMS4yMzc5NiAwLjUyMDUxNiAxLjIxNTIyIDAuNTQyMTlDMS4xOTI0OSAwLjU2Mzg2NCAxLjE3Njk4IDAuNTkyMDIgMS4xNzA4MiAwLjYyMjgyM0MwLjU4MjYyOCAyLjcxODkzIDEuNjc5NTcgNC41MDc5NiAxLjk2MjIxIDQuOTIwNDZDMS45NjU5IDQuOTI1NzcgMS45Njk5MSA0LjkzMDgxIDEuOTc0MjEgNC45MzU1N1oiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl8yMzNfMjk1KSIvPgo8cGF0aCBkPSJNOS41MTAyIDcuNjEwNTlDOS43ODA4OCA3LjYxMDU5IDkuOTk1NyA3LjM3MDU5IDkuOTk1NyA3LjA3ODgyVjYuODk1MjlDOS45OTU3IDYuMTggOS40NjI5NCA1LjU5NjQ3IDguODA5ODggNS41OTY0N0g4LjQ4MzM1VjUuMDMxNzZDOC40ODMzNSA0LjczNTI5IDguMjY0MjMgNC41IDcuOTk3ODUgNC41QzcuNzMxNDcgNC41IDcuNTEyMzUgNC43NCA3LjUxMjM1IDUuMDMxNzZWNS41OTY0N0g3LjE4NTgyQzYuNTMyNzYgNS41OTY0NyA2IDYuMTggNiA2Ljg5NTI5VjcuNzMyOTRDNiA4LjQ0ODI0IDYuNTMyNzYgOS4wMzE3NiA3LjE4NTgyIDkuMDMxNzZIOC44MDk4OEM4LjkyNTg5IDkuMDMxNzYgOS4wMjA0MSA5LjEzNTI5IDkuMDIwNDEgOS4yNjcwNlYxMC4xMDQ3QzkuMDIwNDEgMTAuMjMxOCA4LjkyNTg5IDEwLjM0IDguODA5ODggMTAuMzRINy4xODU4MkM3LjA2OTgyIDEwLjM0IDYuOTc1MyAxMC4yMzY1IDYuOTc1MyAxMC4xMDQ3VjkuOTIxMThDNi45NzUzIDkuNjI0NzEgNi43NTYxOCA5LjM4OTQxIDYuNDg5OCA5LjM4OTQxQzYuMjIzNDIgOS4zODk0MSA2LjAwNDMgOS42Mjk0MSA2LjAwNDMgOS45MjExOFYxMC4xMDQ3QzYuMDA0MyAxMC44MiA2LjUzNzA2IDExLjQwMzUgNy4xOTAxMiAxMS40MDM1SDcuNTE2NjVWMTEuOTY4MkM3LjUxNjY1IDEyLjI2NDcgNy43MzU3NyAxMi41IDguMDAyMTUgMTIuNUM4LjI2ODUzIDEyLjUgOC40ODc2NSAxMi4yNiA4LjQ4NzY1IDExLjk2ODJWMTEuNDAzNUg4LjgxNDE4QzkuNDY3MjQgMTEuNDAzNSAxMCAxMC44MiAxMCAxMC4xMDQ3VjkuMjY3MDZDMTAgOC41NTE3NiA5LjQ2NzI0IDcuOTY4MjQgOC44MTQxOCA3Ljk2ODI0SDcuMTkwMTJDNy4wNzQxMSA3Ljk2ODI0IDYuOTc5NTkgNy44NjQ3MSA2Ljk3OTU5IDcuNzMyOTRWNi44OTUyOUM2Ljk3OTU5IDYuNzY4MjQgNy4wNzQxMSA2LjY2IDcuMTkwMTIgNi42Nkg4LjgxNDE4QzguOTMwMTggNi42NiA5LjAyNDcgNi43NjM1MyA5LjAyNDcgNi44OTUyOVY3LjA3ODgyQzkuMDIwNDEgNy4zNzUyOSA5LjIzOTUzIDcuNjEwNTkgOS41MTAyIDcuNjEwNTlaIiBmaWxsPSJ3aGl0ZSIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyXzIzM18yOTUiIHgxPSIxIiB5MT0iMC41IiB4Mj0iMTUuOTY0NCIgeTI9IjE0LjQ2NjciIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj4KPHN0b3Agc3RvcC1jb2xvcj0iI0VGNzQ3NCIvPgo8c3RvcCBvZmZzZXQ9IjEiIHN0b3AtY29sb3I9IiNDRDAxMDEiLz4KPC9saW5lYXJHcmFkaWVudD4KPC9kZWZzPgo8L3N2Zz4K");
        }
        &.orange {
          background-image: url("data:image/svg+xml;base64,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");
        }
        &.optional {
          background-image: url("data:image/svg+xml;base64,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");
        }
      }
    }
  }
}
.distribute-with-pod-foods{
  .frost-panel {
    text-align: center;
    @include media(md) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    &__cell-left {
      text-align: center;
      padding: 1em 1em 0 1em;
      @include media(md) {
        flex: 1;
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
        overflow: hidden;
        border-right: 1px solid rgba(white, .3);
        backdrop-filter: blur(12px);
        text-align: left;
        padding: 3em 4em;
      }
      h2 {
        font-size: 24px;
        margin: 1em 0 0 0;
      }
      p.caption {
        color: white;
        margin: 0.9em 0;
      }
      .el-button {
        min-width: 240px;
        border-radius: 64px;
        padding: 15px 20px;
        margin-top: 10px;
      }
    }
    &__cell-right {
      padding-bottom: 2em;
      @include media(md) {
        flex: 1;
        text-align: center;
        padding: 3em 4em;
      }
    }
  }
}
</style>
